import { useState, useMemo } from "react";
import { Loader2, Upload, Check, Radio, Calendar, FileUp } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import {
  useLoggedDevices,
  useDeviceTimeRange,
  useCreateSessionFromLog,
  useUploadGpx,
} from "@/hooks/useNetworkCoverage";

interface NewSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = "device" | "timeRange" | "gpx" | "confirm";

export function NewSessionDialog({
  open,
  onOpenChange,
}: NewSessionDialogProps) {
  const [step, setStep] = useState<Step>("device");
  const [selectedDevice, setSelectedDevice] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [gpxFile, setGpxFile] = useState<File | null>(null);
  const [createdSessionId, setCreatedSessionId] = useState<string | null>(null);

  const { data: loggedDevices = [], isLoading: isLoadingDevices } =
    useLoggedDevices();
  const { data: timeRange, isLoading: isLoadingRange } =
    useDeviceTimeRange(selectedDevice);
  const createSession = useCreateSessionFromLog();
  const uploadGpx = useUploadGpx();

  const selectedDeviceData = loggedDevices.find(
    (d) => d.device_uid === selectedDevice,
  );

  const formatDateTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateTimeLocal = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toISOString().slice(0, 16);
  };

  const handleDeviceSelect = (deviceUid: string) => {
    setSelectedDevice(deviceUid);
    setStartTime("");
    setEndTime("");
  };

  const handleNext = () => {
    if (step === "device" && selectedDevice) {
      setStep("timeRange");
      // Pre-fill with device's available range
      if (timeRange) {
        setStartTime(formatDateTimeLocal(timeRange.first_timestamp));
        setEndTime(formatDateTimeLocal(timeRange.last_timestamp));
      }
    } else if (step === "timeRange" && startTime && endTime) {
      setStep("gpx");
    } else if (step === "gpx") {
      setStep("confirm");
    }
  };

  const handleBack = () => {
    if (step === "timeRange") setStep("device");
    else if (step === "gpx") setStep("timeRange");
    else if (step === "confirm") setStep("gpx");
  };

  const handleCreate = async () => {
    const startMs = new Date(startTime).getTime();
    const endMs = new Date(endTime).getTime();

    try {
      const session = await createSession.mutateAsync({
        deviceUid: selectedDevice,
        deviceName: selectedDeviceData?.device_uid,
        startTime: startMs,
        endTime: endMs,
      });

      setCreatedSessionId(session.id);

      // Upload GPX if provided
      if (gpxFile) {
        await uploadGpx.mutateAsync({
          sessionId: session.id,
          gpxFile,
        });
      }

      // Reset and close
      resetForm();
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const resetForm = () => {
    setStep("device");
    setSelectedDevice("");
    setStartTime("");
    setEndTime("");
    setGpxFile(null);
    setCreatedSessionId(null);
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      resetForm();
    }
    onOpenChange(open);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setGpxFile(file);
    }
  };

  const isCreating = createSession.isPending || uploadGpx.isPending;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create New Session</DialogTitle>
          <DialogDescription>
            Create a coverage session from device log data and optionally upload
            a GPX track.
          </DialogDescription>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex items-center justify-between mb-6">
          {(["device", "timeRange", "gpx", "confirm"] as Step[]).map(
            (s, index) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === s
                      ? "bg-primary text-primary-foreground"
                      : index <
                          ["device", "timeRange", "gpx", "confirm"].indexOf(
                            step,
                          )
                        ? "bg-primary/20 text-primary"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  {index + 1}
                </div>
                {index < 3 && (
                  <div
                    className={`w-12 h-0.5 mx-1 ${
                      index <
                      ["device", "timeRange", "gpx", "confirm"].indexOf(step)
                        ? "bg-primary"
                        : "bg-muted"
                    }`}
                  />
                )}
              </div>
            ),
          )}
        </div>

        {/* Step 1: Select Device */}
        {step === "device" && (
          <div className="space-y-4">
            <Label>Select Device</Label>
            {isLoadingDevices ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : loggedDevices.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-muted-foreground">
                  No devices with logged data available
                </CardContent>
              </Card>
            ) : (
              <Select value={selectedDevice} onValueChange={handleDeviceSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a device..." />
                </SelectTrigger>
                <SelectContent>
                  {loggedDevices.map((device) => (
                    <SelectItem
                      key={device.device_uid}
                      value={device.device_uid}
                    >
                      <div className="flex items-center gap-2">
                        <Radio className="h-4 w-4" />
                        <span>{device.device_uid}</span>
                        <span className="text-muted-foreground text-xs">
                          ({device.sample_count} samples)
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {selectedDevice && timeRange && (
              <Card>
                <CardContent className="p-4 text-sm">
                  <p className="text-muted-foreground">Available data range:</p>
                  <p>
                    {formatDateTime(timeRange.first_timestamp)} →{" "}
                    {formatDateTime(timeRange.last_timestamp)}
                  </p>
                  <p className="text-muted-foreground mt-1">
                    {timeRange.sample_count} samples
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 2: Select Time Range */}
        {step === "timeRange" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="startTime">Start Time</Label>
              <DateTimePicker
                id="startTime"
                value={startTime}
                onChange={(v) => setStartTime(v)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endTime">End Time</Label>
              <DateTimePicker
                id="endTime"
                value={endTime}
                onChange={(v) => setEndTime(v)}
              />
            </div>

            {timeRange && (
              <p className="text-sm text-muted-foreground">
                Available: {formatDateTime(timeRange.first_timestamp)} →{" "}
                {formatDateTime(timeRange.last_timestamp)}
              </p>
            )}
          </div>
        )}

        {/* Step 3: Upload GPX */}
        {step === "gpx" && (
          <div className="space-y-4">
            <Label>Upload GPX Track (Optional)</Label>
            <Card
              className={`border-2 border-dashed transition-colors ${
                gpxFile ? "border-primary bg-primary/5" : "border-muted"
              }`}
            >
              <CardContent className="p-8">
                <input
                  type="file"
                  accept=".gpx"
                  onChange={handleFileChange}
                  className="hidden"
                  id="gpx-upload"
                />
                <label
                  htmlFor="gpx-upload"
                  className="flex flex-col items-center cursor-pointer"
                >
                  {gpxFile ? (
                    <>
                      <Check className="h-12 w-12 text-primary mb-2" />
                      <p className="font-medium">{gpxFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Click to change file
                      </p>
                    </>
                  ) : (
                    <>
                      <FileUp className="h-12 w-12 text-muted-foreground mb-2" />
                      <p className="font-medium">Drop GPX file here</p>
                      <p className="text-sm text-muted-foreground">
                        or click to browse
                      </p>
                    </>
                  )}
                </label>
              </CardContent>
            </Card>
            <p className="text-sm text-muted-foreground">
              GPX timestamps will be matched with the selected time range.
            </p>
          </div>
        )}

        {/* Step 4: Confirm */}
        {step === "confirm" && (
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Device:</span>
                  <span className="font-medium">{selectedDevice}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Start:</span>
                  <span>{new Date(startTime).toLocaleString("de-CH")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">End:</span>
                  <span>{new Date(endTime).toLocaleString("de-CH")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">GPX:</span>
                  <span>{gpxFile ? gpxFile.name : "None"}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={step === "device" || isCreating}
          >
            Back
          </Button>

          {step === "confirm" ? (
            <Button offlineEdit onClick={handleCreate} disabled={isCreating}>
              {isCreating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Create Session
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={
                (step === "device" && !selectedDevice) ||
                (step === "timeRange" && (!startTime || !endTime))
              }
            >
              Next
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
